'use client';

import type React from 'react';
import { useState } from 'react';
import Image from 'next/image';

import { Star, Upload, X } from 'lucide-react';
import { useUploadPublicImage } from '@/hooks/api/dashboard/file-service';
import { toast } from '@/components/ui/use-toast'; // your custom toast
import ModalResponsive, { ModalResponsiveFooter } from '@/components/commons/modal-responsive';
import { cn } from '@/lib/utils';
import { Form, FormikProps } from 'formik';
import { StoreCustomerUnreviewedProduct } from '@/types/http';
import { MyReviewElementDataType } from '@/components/sections/builder/elements/body/customer/customer_review/type';
import { Button } from '@/components/ui/button';

interface ReviewFormValues {
  rating: number;
  comment: string;
  media_files: Array<{
    id: string;
    thumbnail: string;
    file: string;
  }>;
}

interface ReviewModalProps {
  content: MyReviewElementDataType;
  openModal: boolean;
  setOpenModal: (open: boolean) => void;
  selectedProduct: StoreCustomerUnreviewedProduct | null;
  formikProps: FormikProps<ReviewFormValues>;
  isLoading: boolean;
  generateUniqueId: () => string;
  title?: string;
}

export default function ReviewModal({
  content,
  setOpenModal,
  openModal,
  selectedProduct,
  formikProps,
  isLoading,
  generateUniqueId,
  title,
}: ReviewModalProps) {
  const { handleSubmit, values, errors, touched, setFieldValue, setFieldError } = formikProps;

  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);

  const { mutateAsync: mutateUpload } = useUploadPublicImage();

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    setIsUploadingFiles(true);
    setUploadProgress(0);

    const fileArray = Array.from(files);
    const totalFiles = fileArray.length;

    const uploaded: Array<{ id: string; thumbnail: string; file: string }> = [];

    for (let i = 0; i < fileArray.length; i++) {
      const file = fileArray[i];
      const formData = new FormData();
      formData.append('file', file);

      try {
        const result = await mutateUpload(formData);
        uploaded.push({
          id: generateUniqueId(),
          thumbnail: result?.data.objectUrl || '',
          file: result?.data.objectUrl || '',
        });
        setUploadProgress(Math.round(((i + 1) / totalFiles) * 100));
      } catch (error) {
        if (error instanceof Error) {
          toast({
            title: 'Error',
            description: error.message || 'Something went wrong.',
            variant: 'destructive',
          });
        }
      }
    }

    const newFiles = [...values.media_files, ...uploaded].slice(0, 5);
    setFieldValue('media_files', newFiles);

    setIsUploadingFiles(false);
    setUploadProgress(0);
    e.target.value = '';
  };

  const handleRemoveFile = (index: number) => {
    const newFiles = values.media_files.filter((_, idx) => idx !== index);
    setFieldValue('media_files', newFiles);
  };

  const onCloseModal = (val: boolean) => {
    setOpenModal(val);
  };

  return (
    <ModalResponsive
      className={cn('h-[450px] max-w-2xl')}
      title={title || 'Write a Review'}
      openModal={openModal}
      setOpenModal={(open) => onCloseModal(open)}
    >
      <div className='overflow-y-auto px-5'>
        <Form onSubmit={handleSubmit}>
          {selectedProduct && (
            <div className='space-y-6'>
              {/* Product Info */}
              <div className='flex items-start gap-4 rounded-lg border p-4'>
                <div className='h-16 w-16 flex-shrink-0 overflow-hidden rounded'>
                  {selectedProduct.product_images?.[0] && (
                    <Image
                      src={selectedProduct.product_images[0]}
                      alt={selectedProduct.product_name || ''}
                      width={64}
                      height={64}
                      className='h-full w-full object-cover'
                    />
                  )}
                </div>
                <div className='min-w-0 flex-1'>
                  <h3
                    className='truncate text-sm font-medium'
                    style={{ color: content?.colorScheme?.text }}
                  >
                    {selectedProduct.product_name}
                  </h3>
                  <p
                    className='truncate text-xs'
                    style={{ color: content?.colorScheme?.textSecondary }}
                  >
                    {selectedProduct.item_name}
                  </p>
                </div>
              </div>

              {/* Rating */}
              <div>
                <label className='mb-3 block text-sm font-medium'>
                  Rating <span className='text-red-500'>*</span>
                </label>
                <div className='flex items-center justify-center gap-2'>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <button
                      key={i}
                      type='button'
                      onClick={() => {
                        setFieldValue('rating', i + 1);
                        if (errors.rating && touched.rating) {
                          setFieldError('rating', '');
                        }
                      }}
                      className='transition-colors'
                      disabled={isLoading || isUploadingFiles}
                    >
                      <Star
                        size={32}
                        className={cn(
                          i < values.rating
                            ? 'fill-yellow-400 text-yellow-400'
                            : 'text-gray-300 hover:text-yellow-300',
                          (isLoading || isUploadingFiles) && 'cursor-not-allowed opacity-50'
                        )}
                      />
                    </button>
                  ))}
                </div>
                {errors.rating && touched.rating && (
                  <p className='mt-2 text-xs text-red-500'>{errors.rating}</p>
                )}
              </div>

              {/* Comment */}
              <div>
                <label className='mb-3 block text-sm font-medium'>
                  Review <span className='text-red-500'>*</span>
                </label>
                <div className='relative'>
                  <textarea
                    name='comment'
                    value={values.comment}
                    onChange={(e) => {
                      setFieldValue('comment', e.target.value);
                      if (errors.comment && touched.comment) {
                        setFieldError('comment', '');
                      }
                    }}
                    placeholder='Write your review...'
                    className='h-32 w-full resize-none rounded-lg border border-gray-300 p-3 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500'
                    maxLength={1000}
                    disabled={isLoading || isUploadingFiles}
                  />
                  <div className='absolute bottom-2 right-2 text-xs text-gray-400'>
                    {values.comment.length}/1000
                  </div>
                </div>
                {errors.comment && touched.comment && (
                  <p className='mt-2 text-xs text-red-500'>{errors.comment}</p>
                )}
              </div>

              {/* File Upload */}
              <div>
                <label className='mb-3 block text-sm font-medium'>Upload Photo or Video</label>
                <label
                  className={cn(
                    'block rounded-lg border-2 border-dashed border-gray-300 p-8 text-center transition-colors',
                    isUploadingFiles || isLoading || values.media_files.length >= 5
                      ? 'cursor-not-allowed opacity-50'
                      : 'cursor-pointer hover:border-gray-400'
                  )}
                >
                  <Upload size={24} className='mx-auto mb-2 text-gray-400' />
                  <p className='text-sm text-gray-500'>
                    {isUploadingFiles
                      ? 'Uploading...'
                      : values.media_files.length >= 5
                      ? 'Maximum 5 files reached'
                      : 'Click to upload'}
                  </p>
                  <input
                    type='file'
                    multiple
                    accept='image/*,video/*'
                    className='hidden'
                    onChange={handleFileUpload}
                    disabled={isUploadingFiles || isLoading || values.media_files.length >= 5}
                  />
                </label>

                {/* Upload Progress */}
                {isUploadingFiles && (
                  <div className='mt-4'>
                    <div className='mb-2 flex items-center justify-between'>
                      <span className='text-sm text-gray-600'>Uploading files...</span>
                      <span className='text-sm font-medium text-blue-500'>{uploadProgress}%</span>
                    </div>
                    <div className='h-2 w-full overflow-hidden rounded-full bg-gray-200'>
                      <div
                        className='h-full bg-blue-500 transition-all duration-300 ease-out'
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* File Preview */}
                {values.media_files.length > 0 && (
                  <div className='mt-4 flex flex-wrap gap-3'>
                    {values.media_files.map((file, i) => (
                      <div key={file.id} className='relative h-20 w-20'>
                        {file.file.includes('video') || /\.(mp4|webm|ogg|mov)$/i.test(file.file) ? (
                          <video
                            src={file.file}
                            className='h-full w-full rounded-lg object-cover'
                          />
                        ) : (
                          <Image
                            src={file.file}
                            alt={`Attachment ${i + 1}`}
                            fill
                            className='rounded-lg object-cover'
                            sizes='80px'
                          />
                        )}
                        <button
                          type='button'
                          className='absolute right-1 top-1 rounded-full bg-black/50 p-1 hover:bg-black/70'
                          onClick={() => handleRemoveFile(i)}
                          disabled={isLoading || isUploadingFiles}
                        >
                          <X size={14} className='text-white' />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <p className='mt-2 text-xs text-gray-500'>
                  Maximum 5 files (photo or video). {5 - values.media_files.length} remaining.
                </p>
              </div>
            </div>
          )}

          {/* Footer Actions */}
          <ModalResponsiveFooter className='px-6'>
            <div className='flex gap-3'>
              <Button
                type='button'
                variant='outline'
                onClick={() => onCloseModal(false)}
                disabled={isLoading || isUploadingFiles}
                className='flex-1'
              >
                Cancel
              </Button>
              <Button
                type='submit'
                disabled={isLoading || isUploadingFiles || values.rating === 0}
                className='flex-1'
                style={{
                  backgroundColor: content?.colorScheme?.btnSolidBackgroud,
                  color: content?.colorScheme?.btnSolidlabel,
                  borderColor: content?.colorScheme?.btnOutline,
                }}
              >
                {isLoading ? 'Submitting...' : 'Send Review'}
              </Button>
            </div>
          </ModalResponsiveFooter>
        </Form>
      </div>
    </ModalResponsive>
  );
}
